<template>
  <div class="workbench">
    <div
      id="canvas"
      class="workbench__canvas"
    >
      <div
        v-if="loading"
        class="workbench__loader"
      >
        <loader-dots></loader-dots>
      </div>
      <scroll-panel class="workbench__canvas-content">
        <workbench-row
          ref="viewership"
          :style="{ height: viewershipRowHeight }"
          class="workbench__canvas__las"
        >
          <audience-size
            class="workbench__audience workbench__canvas-block"
            :validStreamingOptions="validStreamingOptions"
            :size="audienceSize"
            :onSelectedViewingStream="onSelectedViewingStream"
            :viewershipOption="viewership"
          />
          <div class="workbench__canvas-block workbench__viewership">
            <viewership
              :networks="networks"
              :selectedNetworks="selectedViewershipNetworks"
              :selectedDayPartNetworks="selectedDayPartNetworks"
              :viewership-data="viewershipData"
            >
            </viewership>
          </div>
        </workbench-row>
        <workbench-row v-if="selectedDayPartNetworks.length > 0">
          <day-parts
            :selectedNetworks="selectedDayPartNetworks"
            :viewershipData="viewershipData"
            :networks="networks"
            :dayPartsConfig="dayPartsCofig"
          />
        </workbench-row>
        <workbench-row
          ref="universe"
          :style="{ height: universeRowHeight }"
          class="workbench__universe"
        >
          <universe-stability
            class="workbench__canvas-block"
            :universe="universeData"
          >
          </universe-stability>
        </workbench-row>
      </scroll-panel>
    </div>
  </div>
</template>

<script>
  const DEFAULT_VIEWERSHIP = 'aalive3'

  import { ResizeSensor } from 'css-element-queries'

  import ScrollPanel from '@/components/common/scrollPanel'
  import WorkbenchRow from '@/views/workbenchCanvas/WorkbenchRow'
  import AudienceSize from './audienceSize'
  import DayParts from './dayParts'
  import Viewership from './viewership'
  import UniverseStability from './universeStability'
  import LoaderDots from '@/components/common/loaderDots'
  import { UPDATE_TARGET_DEFINITION_RELATED_INFORMATION } from '@/appLogic/workbench/common/store/actions'
  import { SET_VIEWING_STREAM, SET_LOADING } from '@/appLogic/workbench/LAS/store/actions'
  import { ACTIVATION_STATUS, TARGET_TYPE } from '@/appLogic/targets/Constants'

  export default {
    components: {
      AudienceSize,
      DayParts,
      Viewership,
      WorkbenchRow,
      ScrollPanel,
      LoaderDots,
      UniverseStability,
    },
    props: {
      workbenchSlottedValue: {
        type: Object,
        default: undefined,
      },
    },
    data() {
      return {
        viewershipRowHeight: null,
        universeRowHeight: null,
      }
    },
    computed: {
      wsv() {
        return this.workbenchSlottedValue
      },
      w() {
        return this.$store.state.workbench
      },
      las() {
        return this.w ? this.w.las_workbench : {}
      },
      lasState() {
        return this.wsv ? this.wsv.lasState : null
      },
      viewershipData() {
        return this.wsv ? this.wsv.viewershipData : {}
      },
      networks() {
        return this.wsv && this.wsv.networks ? this.wsv.networks : []
      },
      audienceSize() {
        return this.wsv ? this.wsv.audienceSize : 0
      },
      viewership() {
        return this.wsv && this.wsv.lasState ? this.wsv.lasState.viewingSource : DEFAULT_VIEWERSHIP
      },
      universeData() {
        return this.wsv ? this.wsv.universeData : {}
      },
      dayPartsCofig() {
        return this.$store.state.appRes.dayParts
      },
      validStreamingOptions() {
        return this.las.validStreamingOptions || ['C3']
      },
      selectedViewershipNetworks() {
        return this.lasState ? this.lasState.selectedViewershipNetworks : []
      },
      selectedDayPartNetworks() {
        return this.lasState ? this.lasState.selectedDayPartNetworks : []
      },
      loading() {
        // Read loading directly from store state since lasState in slottedValue is a snapshot
        return this.las && this.las.loading !== undefined ? this.las.loading : true
      },
      targetDefinition() {
        return this.$store.getters['workbench/newTargetDefinition']
      },
    },
    methods: {
      setRowSizes() {
        if (this.canvasEl && this.canvasEl.clientHeight) {
          let canvasHeight = this.canvasEl.clientHeight
          let vp = 0.7
          let up = 0.3
          // canvas graphs min height
          if (canvasHeight < 480) {
            canvasHeight = 480
          }
          // help fit universe graph on smaller screens
          if (canvasHeight < 650) {
            vp = 0.6
            up = 0.4
          }
          this.viewershipRowHeight = canvasHeight * vp - 15 + 'px'
          this.universeRowHeight = canvasHeight * up - 15 + 'px'
        }
      },
      onSelectedViewingStream(selected) {
        this.$store.dispatch('workbench/' + SET_VIEWING_STREAM, selected)
        this.$store.dispatch('workbench/' + UPDATE_TARGET_DEFINITION_RELATED_INFORMATION)
      },
    },
    mounted() {
      this.canvasEl = document.querySelector('#canvas')
      document.querySelector('html').classList.add('workbench-resize')
      this.setRowSizes()
      ResizeSensor(this.canvasEl, () => {
        this.setRowSizes()
      })
      let initialLoad = false
      if (this.w.workbenchTarget.target === TARGET_TYPE.LAS) {
        initialLoad = !(this.w.workbenchTarget.activation_status === ACTIVATION_STATUS.DRAFT)
      }
      this.$store.dispatch('workbench/' + UPDATE_TARGET_DEFINITION_RELATED_INFORMATION, initialLoad)
    },
    watch: {
      targetDefinition: {
        handler(newVal, oldVal) {
          // Set loading when targetDefinition changes (attributes added/updated)
          // Skip on initial mount when oldVal is undefined
          if (
            oldVal !== undefined &&
            this.w &&
            this.w.workbenchTarget &&
            this.w.workbenchTarget.target === TARGET_TYPE.LAS
          ) {
            // Dispatch SET_LOADING action - las_workbench actions are accessible at workbench level
            this.$store.dispatch('workbench/' + SET_LOADING, true)
          }
        },
        deep: true,
      },
    },
  }
</script>

<style lang="scss">
  $side-panel-width: rem(380);
  $audience-size-width: rem(170);
  $margin-size: rem(10);
  .topArea {
    margin-top: rem(15) !important;
  }

  .workbench {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;

    &__viewership {
      position: relative;
      width: calc(100% - #{$audience-size-width + $margin-size});
      overflow: scroll;
    }

    &__day-parts {
      flex: 1 100%;
      max-width: 100%;
    }

    &__canvas-block {
      position: relative;
      box-sizing: border-box;
      background: #ffffff;
      padding: rem(20);
      @include drop-shadow;

      &__top {
        margin-top: 40px;
      }

      & + & {
        margin-left: $margin-size;
      }
    }

    &__canvas__las {
      padding-top: 0.5rem;
    }

    &__audience {
      width: $audience-size-width;
      flex: none;
    }

    &__universe {
      position: relative;
      width: 100%;
    }

    &__show-level-table {
      .overlay__content {
        height: 50%;
        width: 80%;
      }
    }

    &__show-level-checklist {
      .overlay__content {
        /*height: 460px;*/
        width: 400px;
      }

      .show-level-checklist {
        box-sizing: border-box;
        padding: 32px;
        overflow: hidden;
      }
    }
    &__canvas {
      position: relative;
      display: flex;
      width: 100%;
      height: 100%;
    }

    &__loader {
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      @if ($workbench-loader) {
        z-index: $workbench-loader;
      } @else {
        z-index: 9999;
      }
      background-color: rgba(#ffffff, 0.8);
    }
  }
</style>



// WEBPACK FOOTER //
// LasGraphs.vue?12a79264